import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import {
  Meeting,
  MeetingAssistCue,
  MeetingAssistFeedbackValue,
  MeetingAssistAudienceLevel,
  MeetingAssistMode,
  MeetingAssistPhase,
  MeetingBotStatus,
} from '../../modules/meeting/model/meetingInterfaces'
import useUpdateAssistContext from '../../modules/meeting/hooks/useUpdateAssistContext'
import useUploadAssistContextDocument from '../../modules/meeting/hooks/useUploadAssistContextDocument'
import ParticipantMeetingInfo from '../participants/ParticipantsInMeetingPage'
import { Button } from '../../onsaui'

// Bot status display component with progress dots
const BotStatusDisplay: React.FC<{
  status: MeetingBotStatus
  onRetry?: () => void
  isLoading?: boolean
}> = ({ status, onRetry, isLoading }) => {
  const getStatusConfig = () => {
    switch (status) {
      case MeetingBotStatus.Inactive:
        return {
          dots: [false, false, false],
          text: 'Ready to record',
          subtext: null,
          color: 'text-gray-600',
          showRetry: false,
        }
      case MeetingBotStatus.BotJoining:
        return {
          dots: [true, false, false],
          text: 'Connecting...',
          subtext: 'Onsa is joining your meeting',
          color: 'text-blue-600',
          showRetry: false,
        }
      case MeetingBotStatus.Waiting:
        return {
          dots: [true, true, false],
          text: 'Waiting to be admitted',
          subtext: 'Please admit "Onsa.ai" from the waiting room',
          color: 'text-amber-600',
          showRetry: false,
        }
      case MeetingBotStatus.Recording:
        return {
          dots: [true, true, true],
          text: 'Recording',
          subtext: 'AI cues are active',
          color: 'text-emerald-600',
          showRetry: false,
        }
      case MeetingBotStatus.BotLeaving:
        return {
          dots: [true, true, true],
          text: 'Finishing up...',
          subtext: 'Saving your recording',
          color: 'text-gray-600',
          showRetry: false,
        }
      case MeetingBotStatus.Error:
        return {
          dots: [false, false, false],
          text: 'Connection failed',
          subtext: 'The bot could not join the meeting',
          color: 'text-red-600',
          showRetry: true,
        }
      default:
        return {
          dots: [false, false, false],
          text: 'Unknown status',
          subtext: null,
          color: 'text-gray-600',
          showRetry: false,
        }
    }
  }

  const config = getStatusConfig()

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          {config.dots.map((active, i) => (
            <div
              key={i}
              className={`h-2.5 w-2.5 rounded-full transition-colors ${
                active ? 'bg-emerald-500' : 'bg-gray-300'
              } ${status === MeetingBotStatus.Recording && active ? 'animate-pulse' : ''}`}
            />
          ))}
        </div>
        <div className="flex-1">
          <div className={`font-medium ${config.color}`}>
            {config.text}
            {status === MeetingBotStatus.Recording && (
              <span className="ml-2 inline-block h-2 w-2 animate-pulse rounded-full bg-red-500" />
            )}
          </div>
          {config.subtext && <div className="text-sm text-gray-500">{config.subtext}</div>}
        </div>
        {config.showRetry && onRetry && (
          <Button size="small" variant="outlined" onClick={onRetry} disabled={isLoading}>
            Try Again
          </Button>
        )}
      </div>
    </div>
  )
}

// Saved indicator component
const SavedIndicator: React.FC<{ show: boolean }> = ({ show }) => {
  if (!show) return null
  return (
    <span className="ml-2 text-xs text-emerald-600 transition-opacity duration-300">Saved</span>
  )
}

const InMeetingPage: React.FC<{
  activeMeeting: Meeting | null
  isLoading: boolean
  isRegenerateLoading: boolean
  toggleRecording: () => void
  toggleRegenerateSummary: () => void
  assistCues: MeetingAssistCue[]
  feedbackById: Record<string, MeetingAssistFeedbackValue>
  submitFeedback: (assistId: string, value: MeetingAssistFeedbackValue) => Promise<void>
  requestAssistNow: (meetingId: string, payload?: { focus?: string }) => Promise<boolean | void>
  isAssistNowLoading: boolean
  onMeetingUpdated?: (meeting: Meeting) => void
}> = ({
  activeMeeting,
  isLoading,
  isRegenerateLoading,
  toggleRecording,
  toggleRegenerateSummary,
  assistCues,
  feedbackById,
  submitFeedback,
  requestAssistNow,
  isAssistNowLoading,
  onMeetingUpdated,
}) => {
  const { isSaving, updateAssistContext } = useUpdateAssistContext()
  const { isUploading, uploadAssistContextDocument } = useUploadAssistContextDocument()

  // Form state
  const [mode, setMode] = useState<MeetingAssistMode>(MeetingAssistMode.Sales)
  const [brief, setBrief] = useState('')
  const [audienceLevel, setAudienceLevel] = useState<MeetingAssistAudienceLevel | ''>('')
  const [phase, setPhase] = useState<MeetingAssistPhase | ''>('')
  const [priorityTopics, setPriorityTopics] = useState('')
  const [assistFocus, setAssistFocus] = useState('')

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [docError, setDocError] = useState<string | null>(null)
  const [showSaved, setShowSaved] = useState(false)
  const [recordingError, setRecordingError] = useState<string | null>(null)
  const [copiedSummary, setCopiedSummary] = useState(false)

  // Auto-save debounce ref
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const savedIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize form from meeting data
  useEffect(() => {
    if (!activeMeeting) return

    setMode(activeMeeting.assistContext?.mode ?? MeetingAssistMode.Sales)
    // Combine brief, goal, and agenda into single brief field for display
    const combinedBrief = [
      activeMeeting.assistContext?.brief,
      activeMeeting.assistContext?.goal,
      activeMeeting.assistContext?.agenda,
    ]
      .filter(Boolean)
      .join(' ')
    setBrief(combinedBrief || '')
    setAudienceLevel(activeMeeting.assistContext?.audienceLevel ?? '')
    setPhase(activeMeeting.assistContext?.phase ?? '')
    setPriorityTopics((activeMeeting.assistContext?.priorityTopics || []).join(', '))
    setAssistFocus('')
    setRecordingError(null)
  }, [activeMeeting?.id])

  // Clear recording error when status changes
  useEffect(() => {
    if (activeMeeting?.botStatus !== MeetingBotStatus.Error) {
      setRecordingError(null)
    }
  }, [activeMeeting?.botStatus])

  // Auto-save function
  const performAutoSave = useCallback(async () => {
    if (!activeMeeting) return

    const updated = await updateAssistContext(activeMeeting.id, {
      mode,
      brief,
      audienceLevel: audienceLevel || undefined,
      phase: phase || undefined,
      priorityTopics,
      useAsDefault: false,
    })

    if (updated) {
      onMeetingUpdated?.(updated)
      setShowSaved(true)
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current)
      }
      savedIndicatorTimeoutRef.current = setTimeout(() => setShowSaved(false), 2000)
    }
  }, [activeMeeting, mode, brief, audienceLevel, phase, priorityTopics, updateAssistContext, onMeetingUpdated])

  // Debounced auto-save on blur
  const handleFieldBlur = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    saveTimeoutRef.current = setTimeout(performAutoSave, 500)
  }, [performAutoSave])

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      if (savedIndicatorTimeoutRef.current) clearTimeout(savedIndicatorTimeoutRef.current)
    }
  }, [])

  const handleUploadDocument = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!activeMeeting || !event.target.files?.length) return

    const file = event.target.files[0]
    setDocError(null)
    const maxBytes = 2 * 1024 * 1024
    if (file.size > maxBytes) {
      setDocError('File is too large (max 2MB)')
      event.target.value = ''
      return
    }
    const updated = await uploadAssistContextDocument(activeMeeting.id, file)
    if (updated) {
      onMeetingUpdated?.(updated)
    } else {
      setDocError('Upload failed. Please try again.')
    }
    event.target.value = ''
  }

  const handleRequestAssistNow = async () => {
    if (!activeMeeting) return
    await requestAssistNow(activeMeeting.id, { focus: assistFocus.trim() || undefined })
    setAssistFocus('')
  }

  const handleToggleRecording = () => {
    setRecordingError(null)
    toggleRecording()
  }

  const handleCopySummary = async () => {
    if (!activeMeeting?.summary) return
    try {
      await navigator.clipboard.writeText(activeMeeting.summary)
      setCopiedSummary(true)
      setTimeout(() => setCopiedSummary(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = activeMeeting.summary
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopiedSummary(true)
      setTimeout(() => setCopiedSummary(false), 2000)
    }
  }

  // Cue filtering logic
  const now = Date.now()
  const maxVisibleCues = 3
  const cueTtlMs = 120000
  const pinnedTtlMs = 300000
  const normalizeCueText = (text: string) =>
    text.toLowerCase().replace(/\s+/g, ' ').trim()

  const dislikedTexts = new Set(
    assistCues
      .filter((cue) => feedbackById[cue.id] === MeetingAssistFeedbackValue.Down)
      .map((cue) => normalizeCueText(cue.text))
      .filter(Boolean),
  )

  const filteredCues = assistCues.filter((cue) => {
    const createdAt = Date.parse(cue.createdAt)
    const isDisliked = feedbackById[cue.id] === MeetingAssistFeedbackValue.Down
    const normalizedText = normalizeCueText(cue.text)
    if (isDisliked || dislikedTexts.has(normalizedText) || Number.isNaN(createdAt)) {
      return false
    }
    const isPinned = feedbackById[cue.id] === MeetingAssistFeedbackValue.Up
    const ttl = isPinned ? pinnedTtlMs : cueTtlMs
    return now - createdAt <= ttl
  })

  const pinnedCues = filteredCues
    .filter((cue) => feedbackById[cue.id] === MeetingAssistFeedbackValue.Up)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
  const unpinnedCues = filteredCues
    .filter((cue) => feedbackById[cue.id] !== MeetingAssistFeedbackValue.Up)
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))

  const dedupedByText: MeetingAssistCue[] = []
  const seenText = new Set<string>()
  for (const cue of [...pinnedCues, ...unpinnedCues]) {
    const normalizedText = normalizeCueText(cue.text)
    if (!normalizedText || seenText.has(normalizedText)) continue
    seenText.add(normalizedText)
    dedupedByText.push(cue)
  }
  const visibleCues = dedupedByText.slice(0, maxVisibleCues)

  if (!activeMeeting) {
    return (
      <div className="flex flex-col items-center p-4">
        <header className="w-full max-w-2xl">
          <h1 className="mb-4 text-2xl font-bold">Meeting Assistant</h1>
          <div className="mb-4 flex flex-col items-center rounded border border-yellow-300 bg-yellow-100 p-4">
            <span className="font-semibold">Oops:</span> You are not in a meeting or do not have the
            proper role to run Onsa.ai. Please start a call or ask for host privileges to use the
            bot.
          </div>
        </header>
      </div>
    )
  }

  const participantCount = activeMeeting.participants?.length || 0

  return (
    <div className="flex flex-col items-center p-4">
      <header className="w-full max-w-2xl">
        {/* Meeting info card */}
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-1 text-lg font-semibold text-gray-900">
            {activeMeeting.title || 'Untitled Meeting'}
          </div>
          <div className="text-sm text-gray-500">
            {participantCount} participant{participantCount !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Bot status with progress dots */}
        <BotStatusDisplay
          status={activeMeeting.botStatus}
          onRetry={handleToggleRecording}
          isLoading={isLoading}
        />

        {/* Recording error message */}
        {recordingError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {recordingError}
          </div>
        )}

        {/* Simplified context form */}
        <div className="mb-4 w-full rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              What should I help you with?
              <span className="ml-1 text-gray-400">(optional)</span>
              <SavedIndicator show={showSaved && !isSaving} />
            </label>
            {isSaving && <span className="text-xs text-gray-400">Saving...</span>}
          </div>
          <textarea
            className="mt-2 w-full rounded-lg border border-gray-300 p-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            rows={2}
            placeholder="e.g., Close the renewal discussion, handle pricing objections"
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            onBlur={handleFieldBlur}
          />

          {/* Context documents - visible by default */}
          <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Attach context</span>
              {isUploading && <span className="text-xs text-gray-400">Uploading...</span>}
            </div>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">
                <input
                  type="file"
                  accept=".txt,.md,.pdf,.docx"
                  onChange={handleUploadDocument}
                  disabled={isUploading}
                  className="hidden"
                />
                + Add file
              </label>
              <span className="text-xs text-gray-400">PDF, DOCX, TXT, MD (max 2MB)</span>
            </div>
            {docError && <div className="mt-2 text-xs text-red-600">{docError}</div>}
            {activeMeeting.assistContext?.documents?.length ? (
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                {activeMeeting.assistContext.documents.map((doc) => (
                  <li key={doc.id} className="flex items-center gap-2">
                    <span className="text-gray-400">📄</span>
                    {doc.name}
                    <span className="text-xs text-gray-400">({Math.round(doc.size / 1024)} KB)</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {/* Recording button */}
          {[MeetingBotStatus.Inactive, MeetingBotStatus.Recording, MeetingBotStatus.Error].includes(
            activeMeeting.botStatus,
          ) && (
            <Button
              className="mt-4 w-full"
              onClick={handleToggleRecording}
              disabled={isLoading}
              variant={activeMeeting.botStatus === MeetingBotStatus.Recording ? 'outlined' : 'primary'}
            >
              {isLoading
                ? 'Please wait...'
                : activeMeeting.botStatus === MeetingBotStatus.Recording
                  ? 'Stop Recording'
                  : 'Start Recording'}
            </Button>
          )}

          {/* Advanced options accordion */}
          <div className="mt-4 border-t border-gray-100 pt-4">
            <button
              type="button"
              className="flex w-full items-center justify-between text-sm font-medium text-gray-600 hover:text-gray-900"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <span>Advanced options</span>
              <span className="text-gray-400">{showAdvanced ? '▾' : '▸'}</span>
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4">
                {/* Mode */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Meeting type</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={mode}
                    onChange={(e) => setMode(e.target.value as MeetingAssistMode)}
                    onBlur={handleFieldBlur}
                  >
                    <option value={MeetingAssistMode.Sales}>Sales</option>
                    <option value={MeetingAssistMode.Team}>Team</option>
                    <option value={MeetingAssistMode.Teaching}>Teaching</option>
                    <option value={MeetingAssistMode.Presentation}>Presentation</option>
                    <option value={MeetingAssistMode.Interview}>Interview</option>
                    <option value={MeetingAssistMode.Standup}>Standup</option>
                    <option value={MeetingAssistMode.Casual}>Casual</option>
                    <option value={MeetingAssistMode.Facilitator}>Facilitator</option>
                    <option value={MeetingAssistMode.Consultation}>Consultation</option>
                  </select>
                </div>

                {/* Phase */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Sales phase</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={phase}
                    onChange={(e) => setPhase(e.target.value as MeetingAssistPhase | '')}
                    onBlur={handleFieldBlur}
                  >
                    <option value="">Not set</option>
                    <option value={MeetingAssistPhase.Opening}>Opening</option>
                    <option value={MeetingAssistPhase.Discovery}>Discovery</option>
                    <option value={MeetingAssistPhase.Delivery}>Delivery</option>
                    <option value={MeetingAssistPhase.Objections}>Objections</option>
                    <option value={MeetingAssistPhase.Decision}>Decision</option>
                    <option value={MeetingAssistPhase.Closing}>Closing</option>
                  </select>
                </div>

                {/* Priority topics */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Priority topics</label>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="pricing, timeline, competitors"
                    value={priorityTopics}
                    onChange={(e) => setPriorityTopics(e.target.value)}
                    onBlur={handleFieldBlur}
                  />
                </div>

                {/* Audience level */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Audience level</label>
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={audienceLevel}
                    onChange={(e) => setAudienceLevel(e.target.value as MeetingAssistAudienceLevel | '')}
                    onBlur={handleFieldBlur}
                  >
                    <option value="">Not set</option>
                    <option value={MeetingAssistAudienceLevel.Beginner}>Beginner</option>
                    <option value={MeetingAssistAudienceLevel.Intermediate}>Intermediate</option>
                    <option value={MeetingAssistAudienceLevel.Advanced}>Advanced</option>
                    <option value={MeetingAssistAudienceLevel.Executive}>Executive</option>
                    <option value={MeetingAssistAudienceLevel.Mixed}>Mixed</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* AI-generated context (read-only) */}
          {(activeMeeting.assistContext?.phases?.length ||
            activeMeeting.assistContext?.keyQuestions?.length ||
            activeMeeting.assistContext?.successOutcome) && (
            <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700">
              <div className="mb-2 font-medium text-gray-500">AI-generated context</div>
              {activeMeeting.assistContext.successOutcome && (
                <div className="mb-2">
                  <span className="font-semibold">Best outcome:</span>{' '}
                  {activeMeeting.assistContext.successOutcome}
                </div>
              )}
              {activeMeeting.assistContext.phases?.length ? (
                <div className="mb-2">
                  <div className="font-semibold">Phases</div>
                  <ul className="list-disc pl-4">
                    {activeMeeting.assistContext.phases.map((phaseItem) => (
                      <li key={phaseItem}>{phaseItem}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {activeMeeting.assistContext.keyQuestions?.length ? (
                <div>
                  <div className="font-semibold">Key questions</div>
                  <ul className="list-disc pl-4">
                    {activeMeeting.assistContext.keyQuestions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Participants */}
        <ParticipantMeetingInfo meetingId={activeMeeting.id} />

        {/* Live Assist */}
        <div className="mb-4 w-full max-w-2xl">
          <div className="mb-2 flex flex-col gap-2">
            <h3 className="text-lg font-semibold">Live Assist</h3>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                className="w-full flex-1 rounded-lg border border-gray-300 p-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Ask for a cue (e.g., handle objection, close, recap)"
                value={assistFocus}
                onChange={(e) => setAssistFocus(e.target.value)}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleRequestAssistNow}
                disabled={
                  !activeMeeting ||
                  activeMeeting.botStatus !== MeetingBotStatus.Recording ||
                  isAssistNowLoading
                }
              >
                {isAssistNowLoading ? 'Requesting...' : 'Request cue'}
              </Button>
            </div>
          </div>
          {visibleCues.length === 0 ? (
            <div className="text-sm text-gray-500">
              {activeMeeting.botStatus === MeetingBotStatus.Recording
                ? 'Listening for cues...'
                : 'Start recording to get live suggestions'}
            </div>
          ) : (
            <div className="space-y-2">
              {visibleCues.map((cue) => (
                <div key={cue.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="mb-1 text-xs font-medium uppercase text-gray-400">{cue.type}</div>
                  <div className="text-sm text-gray-900">{cue.text}</div>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => submitFeedback(cue.id, MeetingAssistFeedbackValue.Up)}
                      disabled={feedbackById[cue.id] === MeetingAssistFeedbackValue.Up}
                    >
                      👍
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => submitFeedback(cue.id, MeetingAssistFeedbackValue.Down)}
                      disabled={feedbackById[cue.id] === MeetingAssistFeedbackValue.Down}
                    >
                      👎
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        {(activeMeeting.transcript || activeMeeting.summary) && (
          <div className="w-full max-w-2xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Meeting Summary</h3>
              {activeMeeting.summary && (
                <button
                  onClick={handleCopySummary}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  {copiedSummary ? 'Copied!' : 'Copy'}
                </button>
              )}
            </div>
            {activeMeeting.summary && (
              <div className="mb-2 whitespace-pre-wrap text-sm">{activeMeeting.summary}</div>
            )}
            <Button
              onClick={toggleRegenerateSummary}
              disabled={isRegenerateLoading}
              size="small"
              variant="outlined"
            >
              {isRegenerateLoading ? 'Regenerating...' : 'Regenerate Summary'}
            </Button>
          </div>
        )}

        {/* Transcript */}
        {activeMeeting.transcript && (
          <div className="mt-4 w-full max-w-2xl">
            <h3 className="mb-2 text-lg font-semibold">Meeting Transcript</h3>
            <div className="whitespace-pre-wrap text-sm">{activeMeeting.transcript}</div>
          </div>
        )}
      </header>
    </div>
  )
}

export default InMeetingPage
